import { User, Users, Target, MessageCircle, ScrollText, Sprout } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

const ASPECTS = [
  { icon: User, label: "Who asked" },
  { icon: Users, label: "Asked to" },
  { icon: Target, label: "Purpose" },
  { icon: MessageCircle, label: "Response" },
  { icon: ScrollText, label: "Commentary" },
  { icon: Sprout, label: "Practical application" },
];

export const QuestionGod = () => (
  <section id="questiongod" className="relative py-16 md:py-24 overflow-hidden">
    <div
      aria-hidden
      className="pointer-events-none absolute -top-8 right-2 md:right-16 font-serif italic leading-none text-plum/[0.05] select-none text-[14rem] md:text-[22rem]"
    >
      ?
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <Reveal className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-plum/10 text-plum text-xs font-semibold px-3 py-1.5 mb-5">
            <span className="font-serif italic text-sm leading-none">?</span>
            An added feature in the Bible reader
          </span>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-plum mb-3">
            The QuestionGod Project
          </p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink text-balance">
            Curiosity Breeds Intimacy.
          </h2>
          <p className="mt-5 font-serif italic text-xl md:text-2xl text-green-dark leading-relaxed max-w-xl">
            The question is the invitation. God asked the first question; He's
            never stopped welcoming yours.
          </p>
          <div className="mt-6 space-y-4 text-ink/75 leading-relaxed max-w-xl">
            <p>
              QuestionGod is custom content we built right into the Bible
              reader. It surfaces and studies the questions of Scripture:
              wonder, lament, rebuke, and invitation, to encourage curiosity and
              show why they matter, in the Bible and in our daily lives, for
              intimacy with God and with one another.
            </p>
            <p>
              We challenge faith traditions that discourage questioning and aim
              to restore a childlike wonder and awe. To "Don't question God," we
              say: He can handle our questions, and He uses them as a door to
              relationship.
            </p>
          </div>
          <p className="mt-6 font-serif italic text-lg md:text-xl text-ink">
            Questioning God isn't rebellion. It's relationship.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-6">
          <div
            data-testid="questiongod-card"
            className="bg-paperwhite rounded-3xl border border-ink/5 shadow-sm p-8 md:p-10"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-plum/10 flex items-center justify-center">
                <span className="font-serif italic text-2xl text-plum leading-none">?</span>
              </div>
              <div>
                <h3 className="font-serif text-2xl text-ink leading-snug">
                  Every question, marked in the text
                </h3>
                <p className="text-ink/70 mt-1 leading-relaxed">
                  As you read, each question in Scripture carries a small
                  question icon. Tap it to open its QuestionGod card.
                </p>
              </div>
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-ink/40 mb-3">
              Each card shows
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ASPECTS.map((a) => {
                const Icon = a.icon;
                return (
                  <div
                    key={a.label}
                    data-testid={`qg-aspect-${a.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="flex items-center gap-2 rounded-xl bg-sage/70 border border-ink/5 px-3 py-2.5"
                  >
                    <Icon className="w-4 h-4 text-plum shrink-0" strokeWidth={1.75} />
                    <span className="text-xs font-medium text-ink/80 leading-tight">{a.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
